/* src/views/jour/JourStockCtrl.jsx — L'ÉCRAN STOCK d'Aujourd'hui (lot 3) :
   le dessin de JourStockProto nourri de stock_vue (le central), le catalogue Kit / Opérationnel de la config, les feuilles
   Dépôt / Courses / Produit. Les OUTILS HISTORIQUES du stock (achats, mois,
   corrections) restent montés dessous, repliés — rien ne se supprime. */

import { useState, useEffect, useMemo, useCallback } from 'react'
import Feuille from '../accueil/Feuille'
import { JourStockProto } from './proto/JourStockProto'
import { FeuilleDepot, FeuilleCourses, FeuilleProduit } from './proto/FeuillesJour'
import { fetchStockVue } from '../../lib/stock-signalements-api'
import { splitConsommables } from '../../lib/consommables-helpers'
import { sousSeuil } from '../../lib/stock-alerte'
import { uid } from '../../lib/format'

const Erreur = ({ texte }) => (texte
  ? <p role="alert" data-testid="stock-erreur" style={{ marginTop: 10, fontSize: 12,
      color: '#8f2f1d' }}>{texte}</p> : null)

export default function JourStockCtrl({ cfg, currentUser, stockApi,
  applyStockSnapshot, enfantsOutils }) {
  const [vueDonnees, setVueDonnees] = useState(null)   // stock_vue
  const [erreur, setErreur] = useState(null)
  const [vue, setVue] = useState('central')
  const [feuille, setFeuille] = useState(null)         // 'depot'|'courses'|{produit}
  const [depot, setDepot] = useState({ ou: 'logement', logement: null, qtes: {} })
  const [envoi, setEnvoi] = useState(false)

  const charger = useCallback(() => {
    fetchStockVue()
      .then((v) => { setVueDonnees(v); setErreur(null) })
      .catch((e) => { console.error('[jour] stock_vue', e?.message || e)
        setErreur('La lecture du stock a échoué.') })
  }, [])
  useEffect(() => { const id = setTimeout(charger, 0); return () => clearTimeout(id) }, [charger])

  const { kitProduits, operProduits } = useMemo(() => splitConsommables(cfg), [cfg])
  const parCs = useMemo(() => {
    const m = {}
    for (const c of (vueDonnees && vueDonnees.central) || []) m[c.cs_id] = c
    return m
  }, [vueDonnees])
  const tous = useMemo(() => [...kitProduits, ...operProduits], [kitProduits, operProduits])
  const etatDe = useCallback((c) => {
    const e = parCs[c.id] || { qty: 0, seuil: 0 }
    return { ...c, qty: e.qty || 0, seuil: e.seuil || 0,
      sous: sousSeuil({ qtyDispo: e.qty || 0, seuilMin: e.seuil || 0 }) }
  }, [parCs])

  const stats = useMemo(() => {
    const etats = tous.map(etatDe)
    return {
      sousSeuil: etats.filter((e) => e.sous).length,
      suivis: etats.length,
    }
  }, [tous, etatDe])

  const groupeDe = (liste, titre) => ({
    titre, sous: `${liste.length} produit${liste.length > 1 ? 's' : ''}`,
    lignes: liste.map(etatDe).map((e) => ({
      nom: e.nom, qty: e.qty, seuil: e.seuil, sousSeuil: e.sous,
      pct: e.seuil > 0 ? Math.min(100, Math.round((e.qty / (e.seuil * 2)) * 100))
        : (e.qty > 0 ? 100 : 0),
      testId: `stock-produit-${e.id}`,
      onOuvrir: () => setFeuille({ produit: e.id }),
    })),
  })

  const courses = useMemo(() => tous.map(etatDe).filter((e) => e.sous)
    .map((e) => ({ nom: e.nom, reste: e.qty, seuil: e.seuil,
      aRacheter: Math.max(0, e.seuil * 2 - e.qty) })), [tous, etatDe])

  const produitOuvert = feuille && feuille.produit
    ? etatDe(tous.find((c) => c.id === feuille.produit) || { id: feuille.produit })
    : null

  // ⚠️ LA FEUILLE DE DÉPÔT SERT À POSER DES PRODUITS, PAS À ACHETER
  // (arbitrage Dimitri 2026-08-27) : elle n'écrit JAMAIS dans la compta.
  // Le dépôt EN LOGEMENT = le dépôt existant (débite le central). Un achat
  // au central se saisit avec son prix réel — celui du ticket — dans
  // « Saisir un achat » (les outils, dessous) ; la feuille LE DIT.
  const enregistrerDepot = useCallback(async () => {
    if (envoi || depot.ou !== 'logement') return
    const choisis = tous.filter((c) => (depot.qtes[c.id] || 0) > 0)
    if (!choisis.length) return
    if (!depot.logement) { setErreur('Choisissez le logement du dépôt.'); return }
    setEnvoi(true); setErreur(null)
    const jour = new Date().toISOString().slice(0, 10)
    try {
      const lignesDepot = choisis.map((c) => ({
        csId: c.id, nbPrelevables: depot.qtes[c.id],
        prixUnitaire: Number(c.prix) || 0,
      }))
      const snap = await stockApi.depotUpsert({
        id: uid(), logId: depot.logement, mois: jour.slice(0, 7), date: jour,
        auteur: 'admin', initiales: null, lignes: lignesDepot,
        coutTotal: lignesDepot.reduce((s2, l) => s2 + l.nbPrelevables * l.prixUnitaire, 0),
      })
      if (snap && applyStockSnapshot) applyStockSnapshot(snap)
      setFeuille(null); setDepot({ ou: 'logement', logement: null, qtes: {} })
      charger()
    } catch (e) { setErreur(e?.message || 'Le dépôt a été refusé.') }
    setEnvoi(false)
  }, [envoi, depot, tous, stockApi, applyStockSnapshot, charger])

  const changerSeuil = useCallback(async (csId, delta) => {
    const e = etatDe(tous.find((c) => c.id === csId) || { id: csId })
    try {
      const snap = await stockApi.stockSet(csId, { seuilMin: Math.max(0, e.seuil + delta) })
      if (snap && applyStockSnapshot) applyStockSnapshot(snap)
      charger()
    } catch (err) { setErreur(err?.message || 'Le seuil a été refusé.') }
  }, [tous, etatDe, stockApi, applyStockSnapshot, charger])

  return (
    <>
      <JourStockProto
        stats={stats}
        onDepot={() => setFeuille('depot')}
        onCourses={() => setFeuille('courses')}
        vue={vue} onVue={setVue}
        groupes={vue === 'central' ? [
          groupeDe(kitProduits, 'Kit'),
          groupeDe(operProduits, 'Opérationnel'),
        ] : []}
        note="Le seuil se règle produit par produit. En dessous, le produit remonte dans la liste de courses."
        enfantsVue={vue === 'central' ? null : (
          <div>
            {vue === 'logements' && (
              <p className="note">Les dépôts en logement vivent dans les outils du stock, dessous — la vue par logement viendra avec son lot.</p>
            )}
            {vue === 'couts' && (
              <p className="note">Les coûts du stock vivent dans les outils du stock, dessous — la vue dédiée viendra avec son lot.</p>
            )}
          </div>
        )}
      />
      {enfantsOutils}
      <Erreur texte={erreur} />

      {feuille === 'depot' && (
        <Feuille testId="jour-feuille-depot" titre="Enregistrer un dépôt"
          onFermer={() => setFeuille(null)}
          enfants={
            <FeuilleDepot ou={depot.ou}
              onOu={(ou) => setDepot((d) => ({ ...d, ou }))}
              produits={depot.ou === 'central' ? [] : tous.map((c) => ({ id: c.id, nom: c.nom,
                sous: `${c.unite || 'unité'} · ${(Number(c.prix) || 0).toFixed(2).replace('.', ',')} € l'unité`,
                qte: depot.qtes[c.id] || 0 }))}
              renvoiCentral={depot.ou === 'central' ? (
                <div className="carte" data-testid="depot-renvoi-achat"
                  style={{ margin: '11px 0 0', background: 'var(--ciel-p)',
                    borderColor: '#c3d5e4' }}>
                  <p style={{ fontSize: '12.5px', margin: 0, lineHeight: 1.6,
                    color: 'var(--navy-d)' }}>Un achat au central se saisit avec
                    son prix réel, dans <b>Saisir un achat</b> — les outils du
                    stock, sous cet écran. Cette feuille pose des produits,
                    elle n'écrit rien dans la compta.</p>
                </div>
              ) : null}
              onMoins={(id) => setDepot((d) => ({ ...d,
                qtes: { ...d.qtes, [id]: Math.max(0, (d.qtes[id] || 0) - 1) } }))}
              onPlus={(id) => setDepot((d) => ({ ...d,
                qtes: { ...d.qtes, [id]: (d.qtes[id] || 0) + 1 } }))}
              total={(() => {
                const t = tous.reduce((s, c) => s + (depot.qtes[c.id] || 0) * (Number(c.prix) || 0), 0)
                return t > 0 ? `${t.toFixed(2).replace('.', ',')} €` : null
              })()}
              pret={depot.ou === 'logement'
                && Object.values(depot.qtes).some((q) => q > 0) && !envoi}
              onEnregistrer={enregistrerDepot}
              onAnnuler={() => setFeuille(null)}
              enfantsLogement={depot.ou === 'logement' ? (
                <div className="pastilles" data-testid="depot-choix-logement">
                  {(cfg && cfg.props ? cfg.props.flatMap((p) => (p.logs || [])
                    .flatMap((l) => (l.chambres && l.chambres.length ? l.chambres : [l]))) : [])
                    .map((l) => (
                      <button key={l.id} className={`ag${depot.logement === l.id ? ' on' : ''}`}
                        onClick={() => setDepot((d) => ({ ...d, logement: l.id }))}>{l.nom}</button>
                    ))}
                </div>
              ) : null}
              erreur={<Erreur texte={erreur} />} />
          } />
      )}
      {feuille === 'courses' && (
        <Feuille testId="jour-feuille-courses" titre="La liste de courses"
          onFermer={() => setFeuille(null)}
          enfants={
            <FeuilleCourses lignes={courses}
              onCopier={() => {
                const txt = courses.map((l) => `${l.nom} : +${l.aRacheter}`).join('\n')
                navigator.clipboard && navigator.clipboard.writeText(txt)
                setFeuille(null)
              }} />
          } />
      )}
      {produitOuvert && (
        <Feuille testId="jour-feuille-produit" titre={produitOuvert.nom || 'Produit'}
          onFermer={() => setFeuille(null)}
          enfants={
            <FeuilleProduit
              qty={produitOuvert.qty}
              unite={produitOuvert.unite || 'unité'}
              sousSeuil={produitOuvert.sous}
              phrase={produitOuvert.sous
                ? `Sous le seuil de ${produitOuvert.seuil} — il en manque ${Math.max(0, produitOuvert.seuil * 2 - produitOuvert.qty)} pour revenir au plein.`
                : `Au-dessus du seuil de ${produitOuvert.seuil}.`}
              seuil={produitOuvert.seuil}
              onSeuilMoins={() => changerSeuil(produitOuvert.id, -1)}
              onSeuilPlus={() => changerSeuil(produitOuvert.id, +1)}
              prix={`${(Number(produitOuvert.prix) || 0).toFixed(2).replace('.', ',')} €`}
              categorie={(produitOuvert.categorie ?? produitOuvert.type) === 'kit_accueil' ? 'Kit' : 'Opérationnel'}
              unitePrelevement={produitOuvert.unite || 'unité'}
              onDeposer={() => { setFeuille('depot') }}
              erreur={<Erreur texte={erreur} />} />
          } />
      )}
    </>
  )
}
